// frontend/app/components/lessonPlans/authoring/blockSchemas.js
//
// The registry over every block type the runtime can render: the two schema
// tables joined, and the questions the editor asks about a type.
//
// The editor is generic and driven entirely by those tables, so adding a block
// type is one entry in coreBlockSchemas or loopBlockSchemas plus one case in
// LessonBlockRenderer — never a lesson-specific branch in a form. Keep the
// types in step with components/lessonPlans/LessonBlockRenderer.js; anything
// listed there and missing here is a type an author cannot create.
//
// `phase` is the Loop step a type belongs to, and `evidenceWeight` is what one
// answer weighs towards mastery. Both are author-facing: the palette shows
// them so a reflection does not end up in Wonder, and so nobody publishes a
// lesson whose only evidence can never produce a score.

import { FIELD } from "./blockFields";
import { CORE_BLOCK_SCHEMAS } from "./coreBlockSchemas";
import { LOOP_BLOCK_SCHEMAS } from "./loopBlockSchemas";

export {
  BLOCK_GROUPS,
  EVIDENCE_LEVELS,
  EVIDENCE_WEIGHT,
  evidenceLevelFor,
  FIELD,
} from "./blockFields";

export const BLOCK_SCHEMAS = [...CORE_BLOCK_SCHEMAS, ...LOOP_BLOCK_SCHEMAS];

export const schemaFor = (type) =>
  BLOCK_SCHEMAS.find((schema) => schema.type === type) || null;

/** What one answer to this type weighs towards mastery, or null if it records none. */
export const evidenceWeightFor = (type) => {
  const schema = schemaFor(type);
  if (!schema || schema.evidenceWeight === undefined) return null;
  return schema.evidenceWeight;
};

/** Types that produce evidence a mastery band can actually be built from. */
export const isWeightedEvidence = (type) => (evidenceWeightFor(type) || 0) > 0;

/** The phase a type is built for, or null if it belongs anywhere. */
export const phaseForBlockType = (type) => schemaFor(type)?.phase || null;

/**
 * Types whose phase is part of what they mean, so landing in another one is
 * worth saying out loud. A graded check in Understand is a teacher's call; a
 * reflection in Wonder is asking students to look back before they have looked.
 */
export const belongsToOnePhase = (type) => Boolean(schemaFor(type)?.strictPhase);

/** Cloned so two blocks of a type never share one array or option object. */
const cloneDefault = (value) => {
  if (!Array.isArray(value)) return value;
  return value.map((item) =>
    item && typeof item === "object" ? { ...item } : item
  );
};

/** A new block of `type`, with its defaults and a unique id. */
export const makeBlock = (type, existingIds = []) => {
  const schema = schemaFor(type);
  if (!schema) return null;

  const taken = new Set(existingIds);
  let n = 1;
  let id = `${type}-${n}`;
  while (taken.has(id)) {
    n += 1;
    id = `${type}-${n}`;
  }

  const block = { id, type };
  schema.fields.forEach((field) => {
    if (field.defaultValue !== undefined) {
      block[field.key] = cloneDefault(field.defaultValue);
      return;
    }
    switch (field.kind) {
      case FIELD.STRING_LIST:
        block[field.key] = [""];
        break;
      case FIELD.PROMPT_LIST:
        block[field.key] = [{ key: "prompt_1", label: "" }];
        break;
      case FIELD.OPTIONS:
      case FIELD.MULTI_OPTIONS:
      case FIELD.ORDER_ITEMS:
        block[field.key] = [
          { id: "a", text: "" },
          { id: "b", text: "" },
        ];
        break;
      default:
        break;
    }
  });

  if (schema.type === "multi_select") block.correct_option_ids = [];
  if (schema.type === "ordering") block.correct_order = ["a", "b"];

  return block;
};

/** Fills `{token}` placeholders; a token with no value is left as written. */
const fill = (template, params = {}) =>
  String(template).replace(/\{(\w+)\}/g, (match, token) =>
    Object.prototype.hasOwnProperty.call(params, token) ? String(params[token]) : match
  );

/**
 * Author-facing problems with one block. Returns [] when it is publishable.
 * Deliberately not the same as the runtime's asset resolution check — this
 * catches blanks before the lesson is ever saved.
 *
 * Pass `t` to get the sentences in the author's language: each is a
 * `{field}`-style template key, and the field label is translated first.
 * Without it the English text comes back, which is what the tests assert.
 */
export const validateBlock = (block, { conceptKeys = [], t } = {}) => {
  const lookup = t || ((key) => key);
  const translate = (key, params) => fill(lookup(key), params);
  const schema = schemaFor(block?.type);
  if (!schema) return [translate("Unknown block type \"{type}\"", { type: block?.type })];

  const problems = [];
  const isBlank = (v) => v === null || v === undefined || String(v).trim() === "";

  schema.fields.forEach((field) => {
    const value = block[field.key];
    const label = lookup(field.label);

    if (field.kind === FIELD.TOGGLE) return;

    if (field.kind === FIELD.NUMBER) {
      if (isBlank(value)) {
        if (field.required) problems.push(translate("{field} is required", { field: label }));
        return;
      }
      const parsed = Number(value);
      if (!Number.isInteger(parsed) || parsed < 0) {
        problems.push(translate("{field} must be a whole number", { field: label }));
      }
      return;
    }

    if (field.kind === FIELD.STRING_LIST) {
      const filled = (value || []).filter((item) => !isBlank(item));
      if (field.required && filled.length === 0) {
        problems.push(translate("{field} needs at least one entry", { field: label }));
      }
      return;
    }

    if (field.kind === FIELD.PROMPT_LIST) {
      const entries = value || [];
      const filled = entries.filter((entry) => !isBlank(entry?.label));
      if (field.required && filled.length === 0) {
        problems.push(translate("{field} needs at least one entry", { field: label }));
      }
      if (field.maxItems && filled.length > field.maxItems) {
        problems.push(translate("{field} takes at most {max}", { field: label, max: field.maxItems }));
      }
      // The key is what the student's answer is filed under, so two prompts
      // sharing one would overwrite each other on save.
      const keys = filled.map((entry) => String(entry.key || "").trim());
      if (keys.some((key) => !key)) problems.push(translate("{field} needs a key for each entry", { field: label }));
      if (new Set(keys).size !== keys.length) {
        problems.push(translate("{field} has two entries with the same key", { field: label }));
      }
      return;
    }

    if (
      field.kind === FIELD.OPTIONS ||
      field.kind === FIELD.MULTI_OPTIONS ||
      field.kind === FIELD.ORDER_ITEMS
    ) {
      const filled = (value || []).filter((option) => !isBlank(option?.text));
      if (filled.length < 2) problems.push(translate("{field} needs at least two entries", { field: label }));
      return;
    }

    if (field.kind === FIELD.PWO_REF) {
      if (!block.asset_ref?.source_pwo_id) problems.push(translate("Pick a 3D scene"));
      else if (isBlank(block.asset_ref?.scene_name)) problems.push(translate("Pick a scene"));
      return;
    }

    if (field.kind === FIELD.VIDEO_REF) {
      if (!block.asset_ref?.source_video_id) problems.push(translate("Pick a video"));
      else if (!block.asset_ref?.tag_id && !block.asset_ref?.is_full_video) {
        problems.push(translate("Pick a segment, or mark it as the whole video"));
      }
      return;
    }

    if (field.required && isBlank(value)) {
      problems.push(translate("{field} is required", { field: label }));
    }
  });

  if (block.type === "multiple_choice" && !block.correct_option_id) {
    problems.push(translate("Mark which answer is correct"));
  }
  if (block.type === "multi_select" && !(block.correct_option_ids || []).length) {
    problems.push(translate("Mark at least one correct answer"));
  }
  if (block.concept_key && !conceptKeys.includes(block.concept_key)) {
    problems.push(
      translate("Concept \"{concept}\" is not declared by this lesson", {
        concept: block.concept_key,
      })
    );
  }

  return problems;
};
